import React, { useState } from "react";
import { Link } from "react-router-dom";
import SellerAvatar from "@/components/SellerAvatar";

/**
 * PulseRing — a constellation of top sellers orbiting an invisible
 * center. Each avatar emits a halo whose radius is proportional to
 * their impact score for the day. Hover pauses the orbit. Clicking a
 * star teleports to that seller's store.
 *
 * Why this is fun: nobody else does a galaxy view on their homepage,
 * the halo size *physically demonstrates* their ranking without
 * forcing the viewer to read a number, and the orbit speed gives it
 * the "alive" feel that static avatars don't.
 *
 * Pure CSS — each avatar is placed via `--angle` and a translateX,
 * then the parent ring rotates infinitely.
 */
export default function PulseRing({ data }) {
  const stars = (data?.pulse_ring || []).slice(0, 8);
  const [paused, setPaused] = useState(false);
  if (!stars.length) return null;

  // Compute halo size — biggest score gets biggest halo. The scale is
  // intentionally non-linear so #1 visibly dwarfs #8.
  const scores = stars.map((s) => s.composite || 1);
  const maxScore = Math.max(...scores, 1);

  return (
    <section
      className="relative bg-gradient-to-b from-zinc-950 via-[#0a1628] to-zinc-950 text-white py-14 sm:py-20 overflow-hidden"
      data-testid="beacon-pulse-ring"
    >
      <div className="absolute inset-0 beacon-stars pointer-events-none opacity-60" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(29,120,171,0.35),transparent_55%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-8">
          <p className="overline text-[#1D78AB] mb-3">The Pulse Ring</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
            Bidorah's constellation
          </h2>
          <p className="mt-3 text-sm sm:text-base text-white/70 max-w-2xl mx-auto">
            The bigger the halo, the bigger the week. Hover to pause, tap a
            star to enter their store.
          </p>
        </div>

        <div
          className="relative mx-auto"
          style={{ width: "min(560px, 95vw)", aspectRatio: "1 / 1" }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          {/* The faint ring path itself */}
          <div className="absolute inset-6 rounded-full border border-white/8" />
          <div className="absolute inset-12 rounded-full border border-white/5" />
          <div className="absolute inset-20 rounded-full border border-white/3" />

          {/* Rotating orbit container — children inside counter-rotate
              so faces stay upright. */}
          <div
            className="absolute inset-0 pulse-ring-orbit"
            style={{ animationPlayState: paused ? "paused" : "running" }}
          >
            {stars.map((s, i) => {
              const angle = (i / stars.length) * 360;
              const haloScale = 0.55 + ((s.composite || 0) / maxScore) * 1.0;
              return (
                <div
                  key={s.user_id}
                  className="absolute top-1/2 left-1/2 pulse-ring-slot"
                  style={{
                    "--angle": `${angle}deg`,
                    transform: `translate(-50%, -50%) rotate(${angle}deg) translateX(40%) rotate(-${angle}deg)`,
                  }}
                >
                  <div
                    className="pulse-ring-counter"
                    style={{ animationPlayState: paused ? "paused" : "running" }}
                  >
                    <Link
                      to={`/seller/${s.user_id}`}
                      data-testid={`pulse-star-${i}`}
                      className="group relative flex flex-col items-center"
                    >
                      {/* Halo */}
                      <span
                        className="absolute rounded-full bg-amber-300/25 blur-2xl pulse-halo"
                        style={{
                          width: `${64 * haloScale}px`,
                          height: `${64 * haloScale}px`,
                          top: `${-12 * haloScale}px`,
                        }}
                      />
                      <SellerAvatar
                        picture={s.picture}
                        name={s.username}
                        size={56}
                        className="relative ring-2 ring-white/30 group-hover:ring-amber-300 transition"
                      />
                      <span className="relative mt-1 text-[10px] font-mono text-white/90 bg-zinc-950/60 px-1.5 py-0.5 rounded-full backdrop-blur-sm">
                        @{s.username}
                      </span>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Center label */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-widest text-amber-300/80">
                This week
              </p>
              <p className="font-display font-black text-2xl text-white mt-1">
                {stars.length} stars
              </p>
              <p className="text-xs text-white/50">{data.category_label}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
